import { Component } from '@theme/component';

const desktop = window.matchMedia('(min-width: 990px) and (hover: hover)');

class MarylunaProductComponent extends Component {
  #index = 0;

  connectedCallback() {
    super.connectedCallback();
    this.refs.slides?.addEventListener('scroll', this.#onSlidesScroll, { passive: true });
    this.refs.thumbs?.addEventListener('scroll', this.#updateThumbArrows, { passive: true });
    this.refs.slides?.addEventListener('pointermove', this.#onZoomMove);
    this.refs.slides?.addEventListener('pointerleave', this.#resetZoom);
    this.refs.slides?.addEventListener('dblclick', this.#onDoubleTap);
    this.refs.slides?.addEventListener('touchstart', this.#onTouch, { passive: true, once: true });
    this.#updateThumbArrows();
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.refs.slides?.removeEventListener('scroll', this.#onSlidesScroll);
    this.refs.thumbs?.removeEventListener('scroll', this.#updateThumbArrows);
  }

  get #count() {
    return this.refs.slides?.children.length ?? 0;
  }

  selectSlide(event) {
    this.#goTo(Number(event.target.dataset.index));
  }

  onSlideClick() {
    if (desktop.matches) this.nextSlide();
  }

  nextSlide() {
    this.#goTo((this.#index + 1) % this.#count);
  }

  previousSlide() {
    this.#goTo((this.#index - 1 + this.#count) % this.#count);
  }

  thumbsUp() {
    this.refs.thumbs.scrollBy({ top: -this.refs.thumbs.clientHeight, behavior: 'smooth' });
  }

  thumbsDown() {
    this.refs.thumbs.scrollBy({ top: this.refs.thumbs.clientHeight, behavior: 'smooth' });
  }

  #goTo(index) {
    const track = this.refs.slides;
    if (!track) return;
    this.#index = index;
    this.#resetZoom();
    track.scrollTo({ left: index * track.clientWidth, behavior: desktop.matches ? 'auto' : 'smooth' });
    this.#syncIndicators();
  }

  #syncIndicators() {
    const thumbs = this.querySelectorAll('.ml-pdp__thumb');
    for (const thumb of thumbs) {
      thumb.setAttribute('aria-current', String(Number(thumb.dataset.index) === this.#index));
    }
    const active = thumbs[this.#index];
    const list = this.refs.thumbs;
    if (active && list) {
      const item = active.parentElement;
      if (item.offsetTop < list.scrollTop || item.offsetTop + item.offsetHeight > list.scrollTop + list.clientHeight) {
        list.scrollTo({ top: item.offsetTop, behavior: 'smooth' });
      }
    }
    const progress = this.refs.progress;
    if (progress && this.#count > 1) {
      const travel = progress.parentElement.clientWidth - progress.clientWidth;
      progress.style.transform = `translateX(${(travel * this.#index) / (this.#count - 1)}px)`;
    }
  }

  #onSlidesScroll = () => {
    const track = this.refs.slides;
    const index = Math.round(track.scrollLeft / track.clientWidth);
    if (index !== this.#index) {
      this.#index = index;
      this.#syncIndicators();
    }
  };

  #updateThumbArrows = () => {
    const list = this.refs.thumbs;
    if (!list) return;
    const up = this.querySelector('.ml-pdp__thumbs-arrow--up');
    const down = this.querySelector('.ml-pdp__thumbs-arrow--down');
    if (up) up.hidden = list.scrollTop <= 2;
    if (down) down.hidden = list.scrollTop + list.clientHeight >= list.scrollHeight - 2;
  };

  #onZoomMove = (event) => {
    if (!desktop.matches) return;
    const image = event.target.closest('.ml-pdp__slide')?.querySelector('img');
    if (!image) return;
    const rect = image.getBoundingClientRect();
    image.style.transformOrigin = `${((event.clientX - rect.left) / rect.width) * 100}% ${((event.clientY - rect.top) / rect.height) * 100}%`;
    image.style.transform = 'scale(2.5)';
  };

  #resetZoom = () => {
    for (const image of this.refs.slides?.querySelectorAll('img') ?? []) {
      image.style.transform = '';
    }
  };

  #onDoubleTap = (event) => {
    if (desktop.matches) return;
    const image = event.target.closest('.ml-pdp__slide')?.querySelector('img');
    if (!image) return;
    if (image.style.transform) {
      image.style.transform = '';
      return;
    }
    const rect = image.getBoundingClientRect();
    image.style.transformOrigin = `${((event.clientX - rect.left) / rect.width) * 100}% ${((event.clientY - rect.top) / rect.height) * 100}%`;
    image.style.transform = 'scale(2)';
  };

  #onTouch = () => {
    this.querySelector('.ml-pdp__main')?.classList.add('is-touched');
  };

  selectSize(event) {
    const input = event.target;
    if (this.refs.sizeValue) this.refs.sizeValue.textContent = input.dataset.size;
    if (this.refs.price) this.refs.price.textContent = input.dataset.price;
    if (this.refs.memberPrice && input.dataset.memberPrice) this.refs.memberPrice.textContent = input.dataset.memberPrice;
    if (this.refs.comparePrice && input.dataset.comparePrice) this.refs.comparePrice.textContent = input.dataset.comparePrice;
    if (!this.classList.contains('ml-qv')) {
      const url = new URL(window.location.href);
      url.searchParams.set('variant', input.value);
      history.replaceState(history.state, '', url);
    }
    this.refs.addButton.disabled = false;
    this.refs.error.hidden = true;
  }

  async addToCart(event) {
    event.preventDefault();
    const form = event.target;
    const button = this.refs.addButton;
    if (button.getAttribute('aria-busy') === 'true') return;
    button.setAttribute('aria-busy', 'true');
    this.refs.error.hidden = true;

    const drawer = document.querySelector('maryluna-cart-drawer-component');
    const body = new FormData(form);
    if (drawer) {
      body.append('sections', drawer.dataset.sectionId);
      body.append('sections_url', window.location.pathname);
    }

    try {
      const response = await fetch(`${window.Shopify.routes.root}cart/add.js`, {
        method: 'POST',
        headers: { Accept: 'application/json' },
        body,
      });
      const data = await response.json().catch(() => ({}));
      if (!response.ok) throw new Error(data.description || data.message || this.refs.error.dataset.fallback);
      if (!drawer) {
        window.location.assign(form.dataset.cartUrl);
        return;
      }
      document.dispatchEvent(new CustomEvent('maryluna:cart:added', { detail: data }));
    } catch (error) {
      this.refs.error.textContent = error instanceof TypeError ? this.refs.error.dataset.fallback : error.message;
      this.refs.error.hidden = false;
    } finally {
      button.removeAttribute('aria-busy');
    }
  }

  back() {
    if (history.length > 1 && document.referrer.startsWith(window.location.origin)) {
      history.back();
    } else {
      window.location.href = window.Shopify.routes.root;
    }
  }

  openDialog(event) {
    const dialog = this.querySelector(`#${event.target.dataset.dialog}`);
    if (!(dialog instanceof HTMLDialogElement) || dialog.open) return;
    dialog.showModal();
    document.documentElement.setAttribute('scroll-lock', '');
    dialog.addEventListener('close', () => document.documentElement.removeAttribute('scroll-lock'), { once: true });
  }

  closeDialog(event) {
    event.target.closest('dialog')?.close();
  }

  onDialogClick(event) {
    const dialog = event.target;
    if (event.composedPath()[0] !== dialog) return;
    const rect = dialog.getBoundingClientRect();
    const inside = event.clientX >= rect.left && event.clientX <= rect.right && event.clientY >= rect.top && event.clientY <= rect.bottom;
    if (!inside) dialog.close();
  }
}

if (!customElements.get('maryluna-product-component')) {
  customElements.define('maryluna-product-component', MarylunaProductComponent);
}
